import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LinearProgress } from '../sectepe-ui/index';
import { useAuth } from '../contexts/AuthContext';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { hatRecht } from '../berechtigungen';
import { PageHeader } from '../components/ui';
import { Icon, IconName } from '../components/icons';
import { FLOW_STEP_COUNT, useLinkedInFlow } from '../linkedin';
import { darfSehen, findRoute, sidebarGroups, type NavGroupId } from '../navigation/routes';
import { angezeigteAppRolle } from '../verwaltung/rollen';
import taskService, { Tagesansicht } from '../services/taskService';
import rechnungService from '../services/rechnungService';
import type { OffenePosten } from '../services/erpTypes';
import { euro } from '../services/quoteService';
import { EinrichtungsCheckliste } from '../components/EinrichtungsCheckliste';
import { handbuchUrl } from '../hilfe/handbuch';

interface QuickLink {
  to: string;
  label: string;
  icon: IconName;
  text: string;
}

const QUICK_LINKS: QuickLink[] = [
  { to: '/text-generator', label: 'Text generieren', icon: 'sparkles', text: 'Freier KI-Text: Anzeige, Mail, Beitrag.' },
  { to: '/templates', label: 'Vorlagen', icon: 'template', text: 'Erprobte Prompt-Vorlagen (Templates).' },
  { to: '/workflows', label: 'Meine Workflows', icon: 'workflow', text: 'Mehrstufige KI-Abläufe ausführen.' },
  { to: '/companies', label: 'Firmen', icon: 'building', text: 'Firmenakten und Kontakte.' },
];

/**
 * Fester Wortlaut aus dem Verbesserungsplan (Paket 2 Schritt 7) fuer alle
 * mit der Rolle Mitarbeit -- auch Konten ohne Intermal-Rolle landen dort.
 * Kein Backend-Feld, kein konfigurierbarer Name in der ersten Version.
 * Der Satz zu Keycloak haengt wie auf "Kein Zugriff" an ssoEnabled
 * (Kapitel 4.3 des Plans): ohne Keycloak vergibt die Inhaber:in die Rollen
 * in der Benutzerverwaltung, dann waere der Hinweis falsch.
 */
export const MITARBEIT_HINWEIS =
  'Deine Rolle ist Mitarbeit. Du kannst Inhalte und Vertriebsdaten pflegen, aber keine Belege ausstellen '
  + 'und nichts nach außen versenden. Brauchst du mehr, wende dich an die Inhaber:in deines Mandanten';
export const KEYCLOAK_HINWEIS = '; Rollen werden in Keycloak vergeben.';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 17) return 'Guten Tag';
  return 'Guten Abend';
};

/**
 * Startseite: statt direkt im Text-Generator zu landen, sieht man, wo der
 * LinkedIn-Prozess steht, was heute im Vertrieb ansteht, was im ERP offen
 * ist, und hat die haeufigsten Ziele einen Klick entfernt.
 *
 * Welche Karten erscheinen, folgt der Sidebar (Paket 4 Schritt 4): eine
 * Karte gibt es nur, wenn die Person die Gruppe dort auch sieht. Die
 * ERP-Gruppe enthaelt fuer Mitarbeit nur "Projekte & Zeiten", darum braucht
 * die ERP-Karte zusaetzlich erp.zahlung.lesen (offene Posten sind
 * Buchhaltungsdaten; der Vertrieb sieht sie nicht, Entscheidung 17).
 */
const StartPage: React.FC = () => {
  const { user } = useAuth();
  const { ssoEnabled } = useBerechtigung();
  const navigate = useNavigate();
  const { status, loaded } = useLinkedInFlow();
  const [tag, setTag] = useState<Tagesansicht | null>(null);
  const [tagFehler, setTagFehler] = useState(false);
  const [posten, setPosten] = useState<OffenePosten | null>(null);
  const [postenFehler, setPostenFehler] = useState(false);
  // Karten nur fuer Bereiche, die die Person auch sehen darf (sonst fuehrt
  // jeder Link auf "Kein Zugriff"); Aufgaben und offene Posten werden nur
  // dann geladen.
  const gruppen = sidebarGroups(user);
  const hatGruppe = (id: NavGroupId) => gruppen.some((g) => g.group.id === id);
  const zeigtLinkedIn = hatGruppe('linkedin');
  const zeigtVertrieb = hatGruppe('sales');
  const zeigtErp = hatGruppe('erp') && hatRecht(user, 'erp.zahlung.lesen');
  const ueberfaellig = posten ? posten.rechnungen.filter((r) => r.tageUeberfaellig > 0).length : 0;
  // Wie TopBar und Profil: Inhaber:in und Admin nie als Mitarbeit ansprechen.
  const zeigtRollenHinweis = angezeigteAppRolle(user) === 'mitarbeit';
  const schnellzugriff = QUICK_LINKS.filter((q) => {
    const entry = findRoute(q.to);
    return !entry || darfSehen(user, entry);
  });

  useEffect(() => {
    if (!zeigtVertrieb) return undefined;
    let active = true;
    Promise.resolve()
      .then(() => taskService.heute())
      .then((t) => { if (active) setTag(t ?? null); })
      .catch(() => { if (active) setTagFehler(true); });
    return () => {
      active = false;
    };
  }, [zeigtVertrieb]);

  useEffect(() => {
    if (!zeigtErp) return undefined;
    let active = true;
    Promise.resolve()
      .then(() => rechnungService.offenePosten())
      .then((op) => { if (active) setPosten(op ?? null); })
      .catch(() => { if (active) setPostenFehler(true); });
    return () => {
      active = false;
    };
  }, [zeigtErp]);

  const next = status.nextStep;
  const firstName = user?.name?.split(' ')[0] ?? '';
  // Einstieg ins Anwenderhandbuch im Plattform-Wiki, nur wenn HANDBUCH_URL gesetzt ist.
  const handbuch = handbuchUrl('erster-tag');

  return (
    <div className="page-container-wide">
      <PageHeader
        title={`${greeting()}${firstName ? `, ${firstName}` : ''}`}
        subtitle="Intermal bündelt LinkedIn-Content, Vertrieb, ERP und KI-Werkzeuge. Mit Strg+K springst du überall hin."
        actions={handbuch ? (
          <a
            href={handbuch}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary btn-sm"
            title="Anwenderhandbuch im Wiki öffnen (erster Tag)"
          >
            <Icon name="book-open" size={14} /> Handbuch<span className="sr-only"> (öffnet in neuem Tab)</span>
          </a>
        ) : undefined}
      />

      <div className="start-grid">
        {/* Erscheint nur, solange ein Schluessel oder der Firmenstamm fehlt. */}
        <EinrichtungsCheckliste />

        {zeigtRollenHinweis && (
          <section className="card start-card" aria-label="Deine Rolle">
            <div className="start-card__head">
              <span className="start-card__icon"><Icon name="info" size={18} /></span>
              <h2 className="card-title">Deine Rolle</h2>
              <Link to="/profile" className="start-card__more">Profil <Icon name="arrow-right" size={14} /></Link>
            </div>
            <p className="start-card__text">{MITARBEIT_HINWEIS}{ssoEnabled ? KEYCLOAK_HINWEIS : '.'}</p>
          </section>
        )}

        {zeigtLinkedIn && (
        <section className="card start-card start-card--linkedin" aria-label="LinkedIn-Studio">
          <div className="start-card__head">
            <span className="start-card__icon"><Icon name="linkedin" size={18} /></span>
            <h2 className="card-title">LinkedIn-Studio</h2>
            <Link to="/gesamtprozess" className="start-card__more">Übersicht <Icon name="arrow-right" size={14} /></Link>
          </div>
          <p className="start-card__text">
            {status.doneCount} von {FLOW_STEP_COUNT} Schritten erledigt
          </p>
          <LinearProgress value={status.progressPercent} aria-label="Fortschritt im LinkedIn-Prozess" />
          <div className="start-card__next">
            {!loaded ? (
              <span className="text-sm text-secondary">Status wird geladen…</span>
            ) : next ? (
              <>
                <span className="text-sm text-secondary">Nächster Schritt</span>
                <strong>{next.index} · {next.label}</strong>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate(next.path)}>
                  {next.cta} <Icon name="arrow-right" size={14} />
                </button>
              </>
            ) : (
              <>
                <span className="text-sm text-secondary">Alle Schritte erledigt</span>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/linkedin-post-generator')}>
                  Neuen Beitrag erstellen <Icon name="arrow-right" size={14} />
                </button>
              </>
            )}
          </div>
        </section>
        )}

        {zeigtVertrieb && (
        <section className="card start-card" aria-label="Vertrieb heute">
          <div className="start-card__head">
            <span className="start-card__icon"><Icon name="sun" size={18} /></span>
            <h2 className="card-title">Vertrieb heute</h2>
            <Link to="/vertrieb" className="start-card__more">Cockpit <Icon name="arrow-right" size={14} /></Link>
          </div>
          {tag ? (
            <div className="akte-kennzahlen">
              <Link to="/aufgaben" className={`kennzahl ${tag.summe.ueberfaellig ? 'kennzahl-warn' : ''}`}>
                <span>{tag.summe.ueberfaellig}</span>überfällig
              </Link>
              <Link to="/aufgaben" className="kennzahl"><span>{tag.summe.heute}</span>heute fällig</Link>
              <Link to="/aufgaben" className="kennzahl"><span>{tag.summe.offen}</span>offen gesamt</Link>
            </div>
          ) : tagFehler ? (
            <p className="text-sm text-secondary">Aufgaben konnten nicht geladen werden.</p>
          ) : (
            <p className="text-sm text-secondary">Aufgaben werden geladen…</p>
          )}
          <div className="start-card__links">
            <Link to="/aufgaben">Heute</Link>
            <Link to="/outreach-inbox">Antworten</Link>
            <Link to="/pipeline">Pipeline</Link>
            <Link to="/lead-sync">Leads &amp; Outreach</Link>
            <Link to="/angebote">Angebote</Link>
          </div>
        </section>
        )}

        {zeigtErp && (
        <section className="card start-card" aria-label="ERP">
          <div className="start-card__head">
            <span className="start-card__icon"><Icon name="receipt" size={18} /></span>
            <h2 className="card-title">ERP</h2>
            <Link to="/erp" className="start-card__more">Cockpit <Icon name="arrow-right" size={14} /></Link>
          </div>
          {posten ? (
            <div className="akte-kennzahlen">
              <Link to="/zahlungen?tab=op" className="kennzahl"><span>{posten.anzahl}</span>offene Posten</Link>
              <Link to="/zahlungen?tab=op" className="kennzahl"><span>{euro(posten.offenCents)}</span>offen gesamt</Link>
              <Link to="/rechnungen?status=overdue" className={`kennzahl ${ueberfaellig ? 'kennzahl-warn' : ''}`}>
                <span>{ueberfaellig}</span>überfällig
              </Link>
            </div>
          ) : postenFehler ? (
            <p className="text-sm text-secondary">Offene Posten konnten nicht geladen werden.</p>
          ) : (
            <p className="text-sm text-secondary">Offene Posten werden geladen…</p>
          )}
          <div className="start-card__links">
            <Link to="/rechnungen">Rechnungen</Link>
            <Link to="/zahlungen">Zahlungen</Link>
            <Link to="/mahnwesen">Mahnwesen</Link>
          </div>
        </section>
        )}

        {schnellzugriff.length > 0 && (
        <section className="card start-card" aria-label="Schnellzugriff">
          <div className="start-card__head">
            <span className="start-card__icon"><Icon name="zap" size={18} /></span>
            <h2 className="card-title">Schnellzugriff</h2>
          </div>
          <ul className="start-quick">
            {schnellzugriff.map((q) => (
              <li key={q.to}>
                <Link to={q.to}>
                  <Icon name={q.icon} size={16} />
                  <span>
                    <strong>{q.label}</strong>
                    <small>{q.text}</small>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="start-card__hint">
            <kbd>Strg</kbd> + <kbd>K</kbd> öffnet die Suche nach Seiten und Aktionen.
          </p>
        </section>
        )}
      </div>
    </div>
  );
};

export default StartPage;
